import { ThemeProvider } from "@emotion/react";
import { Box, BoxProps, createTheme, Paper, Typography } from "@mui/material";
import { SFieldProps } from "components/parts";
import { STheme } from "components/styles";
import { getFormError } from "messages";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";

type Props = Pick<UseControllerProps, "name" | "rules"> & {
  type: "excel" | "csv" | "all";
} & Pick<BoxProps, "height"> &
  Partial<Pick<SFieldProps, "label" | "required">>;

/**
 * ファイル選択フィールド
 *
 * ### Usage
 * ```
 * // フィールドはFile型とする。
 * type Fields = { file: File };
 *
 * // API通信の方法
 * const handleClick = () => {
 *   // ファイルはreact-hook-formから取得する。
 *   const fields = methods.getValues();
 *   // リクエストにマッピングして通信
 *   const param: B000000XXXRequest = {...};
 *   B000000XXXX(param).then(...).catch(...);
 * }
 *
 * // name: フィールド名
 * // label: ドロップ先に表示させるファイル名
 * // type: excel、またはcsv
 * // height: 高さを設定する(規定10rem)。幅は親要素のgridTemplateColumnで設定する。
 * // gridColumn / gridRow: 設定可能
 * return (
 * <SFrame gridTemplateColumns="30rem" rowGap="5px">
 *   <SFileField name="fieldName" label="ファイル名" type="excel" />
 *   <SNormalButton label="取込" onClick={handleClick} />
 * </SFrame>
 * ```
 *
 * @param props
 * @returns
 */
export const SFileField = ({
  name,
  rules,
  type,
  height = "10rem",
  label,
  required,
  ...props
}: Props): JSX.Element => {
  const multiple = false;
  const accept: string[] = getTypes(type);
  const { control, setValue } = useFormContext();
  const { field } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: required && {
        value: required,
        message: getFormError("required", label ?? ""),
      },
    },
  });
  const onDrop = useCallback(
    (files) => {
      if (files.length !== 1) return;
      setValue(name, files[0]);
    },
    [name, setValue]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    accept,
    onDrop,
  });
  return (
    <ThemeProvider theme={theme}>
      <Box height={height}>
        <Paper variant="outlined" square {...getRootProps()}>
          <input name={field.name} ref={field.ref} {...getInputProps()} />
          <Typography>
            {getLabel(label ?? "", isDragActive, field.value)}
          </Typography>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

const theme = createTheme(
  {
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            height: "100%",
            display: "flex",
            alignItems: "center",
            border: "1px dashed",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            margin: "auto",
          },
        },
      },
    },
  },
  { ...STheme }
);

const getTypes = (type: string): string[] => {
  if (type === "excel") return getExcelTypes();
  if (type === "csv") return getCsvTypes();
  return [];
};

const getExcelTypes = (): string[] => [
  ".xlsx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls",
  "application/vnd.ms-excel",
];

const getCsvTypes = (): string[] => [".csv", "text/csv"];

const getLabel = (
  label: string,
  isDragActive: boolean,
  file: File | undefined
): string => {
  if (file instanceof File) {
    return `ファイル名: ${file.name}`;
  }
  if (isDragActive) {
    return `${label}をこちらにドロップしてください。`;
  }
  return `${label}をドラッグ&ドロップしてください。`;
};
